import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const DEFAULT_ROOT_MARGIN = "0px 0px -60% 0px";
const DEFAULT_THRESHOLD = 0;
const PROGRAMMATIC_SCROLL_SETTLE_MS = 600;

export type UseScrollSpyProps = {
  /** Section ids to observe, in document order. */
  sectionIds: string[];
  /** Scroll container. Defaults to the viewport when omitted. */
  containerRef?: RefObject<HTMLElement>;
  rootMargin?: string;
  threshold?: number | number[];
};

export type UseScrollSpy = {
  activeId: string;
  scrollTo: (id: string) => void;
  registerSection: (id: string) => (node: HTMLElement | null) => void;
};

/**
 * Tracks which registered section is currently in view and scrolls to one on demand.
 *
 * @author Rajkumar Rathod
 */
export const useScrollSpy = ({
  sectionIds,
  containerRef,
  rootMargin = DEFAULT_ROOT_MARGIN,
  threshold = DEFAULT_THRESHOLD,
}: UseScrollSpyProps): UseScrollSpy => {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? "");
  const nodesRef = useRef<Map<string, HTMLElement>>(new Map());
  const pendingIdRef = useRef<string | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const orderedIds = useMemo(() => sectionIds.join("|"), [sectionIds]);

  const registerSection = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) nodesRef.current.set(id, node);
      else nodesRef.current.delete(id);
    },
    []
  );

  useEffect(() => {
    const ids = orderedIds ? orderedIds.split("|") : [];
    const nodes = ids
      .map((id) => nodesRef.current.get(id))
      .filter((node): node is HTMLElement => !!node);

    if (!nodes.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // A chip click drives the highlight until its smooth scroll settles.
        if (pendingIdRef.current) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        const nextId = visible[0]?.target.id;
        if (nextId) setActiveId(nextId);
      },
      { root: containerRef?.current ?? null, rootMargin, threshold }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [orderedIds, containerRef, rootMargin, threshold]);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, []);

  const scrollTo = useCallback(
    (id: string) => {
      const node = nodesRef.current.get(id);
      if (!node) return;

      pendingIdRef.current = id;
      setActiveId(id);

      const container = containerRef?.current;
      if (container) {
        // scrollIntoView would also scroll every ancestor, the window included.
        const top =
          node.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top, behavior: "smooth" });
      } else {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => {
        pendingIdRef.current = null;
      }, PROGRAMMATIC_SCROLL_SETTLE_MS);
    },
    [containerRef]
  );

  return { activeId, scrollTo, registerSection };
};
